import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { renderMarkdown } from "@/lib/markdown";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Search, Plus, Eye, Archive } from "lucide-react";
import { toast } from "sonner";

const MANAGE_ROLES = ["service_desk_manager", "l3_technician", "super_admin", "msp_owner"];

export default function Knowledge() {
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [body, setBody] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [editing, setEditing] = useState(null); // null | {} | article
  // The category vocabulary comes from its OWN endpoint (the whole tenant's
  // articles), never from body.items -- deriving it from the current result
  // set meant every search or filter narrowed the filter's own options, and a
  // real category outside that page's window became unreachable.
  const [categories, setCategories] = useState([]);

  useEffect(() => { api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {}); }, []);
  const mayManage = !!me && MANAGE_ROLES.includes(me.role);

  const loadCategories = useCallback(async () => {
    try {
      const r = await api.get("/kb/categories");
      setCategories(Array.isArray(r.data) ? r.data : []);
    } catch {
      setCategories([]);
    }
  }, []);
  useEffect(() => { loadCategories(); }, [loadCategories]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/kb", {
        params: { q, category: category || undefined, include_archived: includeArchived, limit: 100 },
      });
      setBody(r.data && typeof r.data === "object" && Array.isArray(r.data.items)
        ? r.data : { items: [], total: 0 });
    } catch {
      setBody({ items: [], total: 0 });
    } finally { setLoading(false); }
  }, [q, category, includeArchived]);

  // Debounced so a search box does not fire a request per keystroke.
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [load]);

  async function save(draft) {
    try {
      if (draft.id) await api.patch(`/kb/${draft.id}`, {
        title: draft.title, body_md: draft.body_md, category: draft.category, tags: draft.tags,
      });
      else await api.post("/kb", {
        title: draft.title, body_md: draft.body_md, category: draft.category, tags: draft.tags,
      });
      setEditing(null);
      await Promise.all([load(), loadCategories()]);
      toast.success(draft.id ? "Article updated" : "Article created");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-mono">Service Desk · KB</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">
            Knowledge Base <span className="text-zinc-500">({body.total})</span>
          </h1>
        </div>
        {mayManage && (
          <button onClick={() => setEditing({})} data-testid="new-article-btn"
            className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-3 py-1.5 text-sm flex items-center gap-1.5 shrink-0">
            <Plus size={15} /> New article
          </button>
        )}
      </div>

      <div className="flex gap-2 flex-wrap items-center">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search articles" data-testid="kb-search"
            className="bg-zinc-950 border border-zinc-800 rounded-md pl-9 pr-3 py-1.5 text-sm w-full focus:outline-none focus:border-blue-500/60" />
        </div>
        <select value={category} onChange={(e) => setCategory(e.target.value)}
          data-testid="kb-category-filter"
          className="bg-zinc-950 border border-zinc-800 rounded-md px-3 py-1.5 text-sm min-w-0 max-w-[10rem] shrink">
          <option value="">All categories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <label className="flex items-center gap-2 text-sm text-zinc-400 select-none">
          <input type="checkbox" checked={includeArchived}
            onChange={(e) => setIncludeArchived(e.target.checked)}
            data-testid="kb-include-archived"
            className="accent-blue-600" />
          Include archived
        </label>
      </div>

      <div className="space-y-2">
        {loading ? <div className="text-zinc-500">Loading…</div>
          : body.items.length === 0
            ? <div className="bg-zinc-950 border border-zinc-900 rounded-md px-4 py-6 text-zinc-500 text-sm">No articles match.</div>
            : body.items.map((a) => (
                <Link key={a.id} to={`/knowledge/${a.id}`} className="block" data-testid={`kb-article-${a.id}`}>
                  <div className="bg-zinc-950 border border-zinc-900 rounded-md px-4 py-3 hover:bg-zinc-900/40 flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-zinc-100 truncate">{a.title}</span>
                        {a.category && (
                          <span className="text-[10px] uppercase tracking-widest font-mono text-blue-300 bg-blue-500/10 border border-blue-500/30 rounded px-1.5 py-0.5">
                            {a.category}
                          </span>
                        )}
                        {a.archived_at && (
                          <span className="text-[10px] uppercase tracking-widest font-mono text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5 flex items-center gap-1">
                            <Archive size={10} /> Archived
                          </span>
                        )}
                      </div>
                      {(a.tags || []).length > 0 && (
                        <div className="flex gap-1 flex-wrap mt-1.5">
                          {a.tags.map((t) => (
                            <span key={t} className="text-[11px] text-zinc-500 bg-zinc-900 border border-zinc-800 rounded px-1.5 py-0.5">
                              {t}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-4 text-[11px] text-zinc-500 font-mono shrink-0">
                      <span className="flex items-center gap-1"><Eye size={12} /> {a.view_count ?? 0}</span>
                      <span>{(a.updated_at || a.created_at || "").slice(0, 10) || "—"}</span>
                    </div>
                  </div>
                </Link>
              ))}
      </div>

      {editing !== null && (
        <ArticleEditor initial={editing} onCancel={() => setEditing(null)} onSave={save} />
      )}
    </div>
  );
}

function ArticleEditor({ initial, onCancel, onSave }) {
  const [draft, setDraft] = useState({
    id: initial.id, title: initial.title || "", body_md: initial.body_md || "",
    category: initial.category || "General", tags: initial.tags || [],
  });
  const [tagsText, setTagsText] = useState((initial.tags || []).join(", "));
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setDraft((p) => ({ ...p, [k]: v }));

  const submit = async () => {
    if (!draft.title.trim()) return toast.error("A title is required.");
    setBusy(true);
    try {
      await onSave({
        ...draft,
        title: draft.title.trim(),
        tags: tagsText.split(",").map((t) => t.trim()).filter(Boolean),
      });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent
        data-testid="article-editor-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-5xl max-h-[85vh] overflow-y-auto"
      >
        <DialogHeader><DialogTitle className="font-display">{draft.id ? "Edit article" : "New article"}</DialogTitle></DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-xs text-zinc-400">Title</span>
            <input value={draft.title} onChange={(e) => set("title", e.target.value)}
              data-testid="article-title" autoFocus
              className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
          </label>
          <label className="block">
            <span className="text-xs text-zinc-400">Category</span>
            <input value={draft.category} onChange={(e) => set("category", e.target.value)}
              data-testid="article-category"
              className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
          </label>
        </div>

        <label className="block">
          <span className="text-xs text-zinc-400">Tags (comma-separated)</span>
          <input value={tagsText} onChange={(e) => setTagsText(e.target.value)}
            data-testid="article-tags"
            className="w-full mt-1 bg-zinc-900 border border-zinc-800 rounded px-3 py-2 text-sm focus:outline-none focus:border-blue-500/60" />
        </label>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <span className="text-xs text-zinc-400">Body (Markdown)</span>
            <textarea
              data-testid="article-body"
              className="w-full h-96 mt-1 font-mono text-sm bg-zinc-900 border border-zinc-800 rounded p-3 focus:outline-none focus:border-blue-500/60"
              value={draft.body_md}
              onChange={(e) => setDraft({ ...draft, body_md: e.target.value })}
            />
          </div>
          <div>
            <span className="text-xs text-zinc-400">Preview</span>
            {/* renderMarkdown is the ONLY sanitised path; never call marked directly here */}
            <div
              data-testid="article-preview"
              className="h-96 mt-1 overflow-auto prose prose-invert max-w-none bg-zinc-900 border border-zinc-800 rounded p-3 text-sm"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(draft.body_md) }}
            />
          </div>
        </div>

        <DialogFooter>
          <button onClick={onCancel} className="border border-zinc-800 rounded px-3 py-1.5 text-sm text-zinc-300">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="article-save"
            className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded px-4 py-1.5 text-sm">
            {busy ? "Saving…" : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
